/**
 * Composant de gestion des templates de notification
 * Permet de configurer les templates avec variables dynamiques
 */
import React, { useState, useEffect, useCallback } from 'react';
import { sanitizeHtml } from '../utils/sanitize';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Bell,
  Mail,
  MessageSquare,
  Smartphone,
  Edit,
  Trash2,
  Plus,
  Save,
  Eye,
  Copy,
  ChevronDown,
  ChevronRight,
  Code,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Zap,
  Info,
  Send,
  Loader2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes par canal
const CHANNEL_ICONS = {
  in_app: Bell,
  email: Mail,
  sms: Smartphone,
  whatsapp: MessageSquare
};

// Couleurs par priorité
const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800'
};

const NotificationTemplates = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [variables, setVariables] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // États pour l'édition
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // États pour le test d'email
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [testEmailTemplate, setTestEmailTemplate] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // État pour les variables ouvertes
  const [openVariableGroups, setOpenVariableGroups] = useState({});

  // Charger les données
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': 'Bearer cookie-auth' };
      
      const [templatesRes, eventTypesRes, variablesRes] = await Promise.all([
        fetch(`${API_URL}/api/notification-templates`, { headers }),
        fetch(`${API_URL}/api/notification-templates/event-types`, { headers }),
        fetch(`${API_URL}/api/notification-templates/variables`, { headers })
      ]);
      
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
      
      if (eventTypesRes.ok) {
        const data = await eventTypesRes.json();
        setEventTypes(data.event_types || []);
      }
      
      if (variablesRes.ok) {
        const data = await variablesRes.json();
        setVariables(data.variables || {});
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les templates',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sauvegarder un template
  const saveTemplate = async (template) => {
    setSaving(true);
    try {
      const isNew = !template.id || template.id.startsWith('new_');
      
      const url = isNew 
        ? `${API_URL}/api/notification-templates`
        : `${API_URL}/api/notification-templates/${template.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer cookie-auth'
        },
        body: JSON.stringify(template)
      });
      
      if (!response.ok) throw new Error('Erreur de sauvegarde');
      
      toast({
        title: 'Succès',
        description: isNew ? 'Template créé' : 'Template mis à jour'
      });
      
      setShowEditor(false);
      setEditingTemplate(null);
      fetchData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Supprimer un template
  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notification-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      
      if (!response.ok) throw new Error('Erreur de suppression');
      
      toast({ title: 'Succès', description: 'Template supprimé' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Prévisualiser un template
  const previewTemplate = async (template) => {
    try {
      const response = await fetch(`${API_URL}/api/notification-templates/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer cookie-auth'
        },
        body: JSON.stringify({
          template_id: template.id,
          subject: template.subject,
          body_text: template.body_text,
          body_html: template.body_html
        })
      });
      
      if (!response.ok) throw new Error('Erreur de prévisualisation');
      
      const data = await response.json();
      setPreviewData(data);
      setShowPreview(true);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Réinitialiser aux valeurs par défaut
  const resetToDefaults = async () => {
    if (!window.confirm('Réinitialiser tous les templates aux valeurs par défaut ? Cette action est irréversible.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notification-templates/reset-defaults`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      
      if (!response.ok) throw new Error('Erreur de réinitialisation');
      
      toast({ title: 'Succès', description: 'Templates réinitialisés' });
      fetchData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Copier une variable dans le presse-papier
  const copyVariable = (variable) => {
    navigator.clipboard.writeText(`{${variable}}`);
    toast({
      title: 'Copié',
      description: `{${variable}} copié dans le presse-papier`
    });
  };

  // Nouveau template vide
  const createNewTemplate = () => {
    setEditingTemplate({
      id: `new_${Date.now()}`,
      name: '',
      description: '',
      event_type: '',
      channels: ['in_app'],
      is_active: true,
      subject: '',
      body_text: '',
      body_html: '',
      priority: 'normal',
      category: 'SYSTEME'
    });
    setShowEditor(true);
  };

  // Ouvrir la modal de test email
  const openTestEmailDialog = (template) => {
    setTestEmailTemplate(template);
    setTestEmailAddress(user?.email || '');
    setShowTestEmailDialog(true);
  };

  // Envoyer l'email de test
  const sendTestEmail = async () => {
    if (!testEmailTemplate) return;
    
    setSendingTestEmail(true);
    try {
      const response = await fetch(
        `${API_URL}/api/notification-templates/test-send/${testEmailTemplate.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer cookie-auth'
          },
          body: JSON.stringify({
            template_id: testEmailTemplate.id,
            to_email: testEmailAddress || null
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'envoi');
      }

      toast({
        title: '✅ Email envoyé',
        description: data.message || `Email de test envoyé à ${testEmailAddress || user?.email}`,
      });
      setShowTestEmailDialog(false);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de l\'envoi de l\'email de test',
        variant: 'destructive'
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Rendu de la liste des templates
  const renderTemplatesList = () => (
    <div className="space-y-3">
      {templates.map((template) => (
        <Card key={template.id} className={!template.is_active ? 'opacity-60' : ''}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-semibold">{template.name}</h4>
                  <Badge className={PRIORITY_COLORS[template.priority]}>
                    {template.priority}
                  </Badge>
                  {!template.is_active && (
                    <Badge variant="outline" className="text-gray-500">Inactif</Badge>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {template.event_type}
                  </span>
                  <span className="flex items-center gap-1">
                    {template.channels?.map((channel) => {
                      const Icon = CHANNEL_ICONS[channel] || Bell;
                      return <Icon key={channel} className="w-3 h-3" />;
                    })}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Bouton Test Email */}
                {template.channels?.includes('email') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    onClick={() => openTestEmailDialog(template)}
                    title="Envoyer un email de test"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => previewTemplate(template)}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingTemplate(template);
                    setShowEditor(true);
                  }}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => deleteTemplate(template.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // Rendu des variables disponibles
  const renderVariablesPanel = () => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Code className="w-4 h-4" />
          Variables Disponibles
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          {Object.entries(variables).map(([groupKey, group]) => (
            <Collapsible
              key={groupKey}
              open={openVariableGroups[groupKey]}
              onOpenChange={(open) => setOpenVariableGroups(prev => ({ ...prev, [groupKey]: open }))}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-2 hover:bg-gray-50 rounded">
                <span className="font-medium text-sm capitalize">{groupKey}</span>
                {openVariableGroups[groupKey] ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="pl-2 space-y-1 mb-2">
                  {Object.entries(group.variables || {}).map(([varName, description]) => (
                    <div
                      key={varName}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs group cursor-pointer hover:bg-gray-100"
                      onClick={() => copyVariable(varName.replace(/[{}]/g, ''))}
                    >
                      <div>
                        <code className="text-purple-600 font-mono">{varName}</code>
                        <p className="text-gray-500 mt-0.5">{description}</p>
                      </div>
                      <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 text-gray-400" />
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </ScrollArea>
      </CardContent>
    </Card>
  );

  // Éditeur de template
  const renderEditor = () => {
    if (!editingTemplate) return null;
    
    return (
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate.id?.startsWith('new_') ? 'Nouveau Template' : 'Modifier le Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-6">
            {/* Formulaire principal */}
            <div className="col-span-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Nom *</label>
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    placeholder="Nom du template"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Type d&apos;événement *</label>
                  <Select
                    value={editingTemplate.event_type}
                    onValueChange={(v) => setEditingTemplate({ ...editingTemplate, event_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((et) => (
                        <SelectItem key={et.value} value={et.value}>
                          {et.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={editingTemplate.description || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  placeholder="Description du template"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Priorité</label>
                  <Select
                    value={editingTemplate.priority}
                    onValueChange={(v) => setEditingTemplate({ ...editingTemplate, priority: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Basse</SelectItem>
                      <SelectItem value="normal">Normale</SelectItem>
                      <SelectItem value="high">Haute</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Canaux</label>
                  <div className="flex gap-2 mt-1">
                    {['in_app', 'email', 'sms', 'whatsapp'].map((channel) => {
                      const Icon = CHANNEL_ICONS[channel];
                      const isActive = editingTemplate.channels?.includes(channel);
                      return (
                        <Button
                          key={channel}
                          type="button"
                          variant={isActive ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            const channels = isActive
                              ? editingTemplate.channels.filter(c => c !== channel)
                              : [...(editingTemplate.channels || []), channel];
                            setEditingTemplate({ ...editingTemplate, channels });
                          }}
                        >
                          <Icon className="w-4 h-4" />
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingTemplate.is_active}
                  onCheckedChange={(v) => setEditingTemplate({ ...editingTemplate, is_active: v })}
                />
                <label className="text-sm">Template actif</label>
              </div>
              
              <Tabs defaultValue="text" className="mt-4">
                <TabsList>
                  <TabsTrigger value="text">Texte</TabsTrigger>
                  <TabsTrigger value="html">HTML (Email)</TabsTrigger>
                </TabsList>
                
                <TabsContent value="text" className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Sujet *</label>
                    <Input
                      value={editingTemplate.subject}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                      placeholder="Sujet du message (variables supportées)"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Corps du message (texte) *</label>
                    <Textarea
                      value={editingTemplate.body_text}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, body_text: e.target.value })}
                      placeholder="Corps du message en texte brut..."
                      rows={8}
                      className="font-mono text-sm"
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="html" className="space-y-3">
                  <div className="flex items-center gap-2 p-2 bg-blue-50 rounded text-sm text-blue-700">
                    <Info className="w-4 h-4" />
                    Le HTML est utilisé pour les emails. Les autres canaux utilisent le texte.
                  </div>
                  <div>
                    <label className="text-sm font-medium">Corps HTML (optionnel)</label>
                    <Textarea
                      value={editingTemplate.body_html || ''}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, body_html: e.target.value })}
                      placeholder="<h1>Titre</h1><p>Message avec {variable}...</p>"
                      rows={12}
                      className="font-mono text-sm"
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Panneau des variables */}
            <div className="col-span-1">
              {renderVariablesPanel()}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => previewTemplate(editingTemplate)}>
              <Eye className="w-4 h-4 mr-2" />
              Prévisualiser
            </Button>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Annuler
            </Button>
            <Button onClick={() => saveTemplate(editingTemplate)} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Modal de prévisualisation
  const renderPreviewModal = () => (
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Prévisualisation du Template</DialogTitle>
        </DialogHeader>
        
        {previewData && (
          <Tabs defaultValue="rendered">
            <TabsList>
              <TabsTrigger value="rendered">Rendu</TabsTrigger>
              <TabsTrigger value="html">HTML brut</TabsTrigger>
            </TabsList>
            
            <TabsContent value="rendered" className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Sujet:</p>
                <p className="font-semibold">{previewData.subject}</p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-2">Corps (texte):</p>
                <pre className="whitespace-pre-wrap text-sm">{previewData.body_text}</pre>
              </div>
              
              {previewData.body_html && (
                <div className="border rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-2">Aperçu Email:</p>
                  <div 
                    className="bg-white"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewData.body_html) }}
                  />
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="html">
              {previewData.body_html && (
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                  {previewData.body_html}
                </pre>
              )}
            </TabsContent>
          </Tabs>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowPreview(false)}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Modal de test email
  const renderTestEmailModal = () => (
    <Dialog open={showTestEmailDialog} onOpenChange={setShowTestEmailDialog}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Envoyer un email de test
          </DialogTitle>
        </DialogHeader>
        
        {testEmailTemplate && (
          <div className="space-y-4 py-4">
            {/* Info template */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">
                Template: <span className="text-gray-900">{testEmailTemplate.name}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Événement: {testEmailTemplate.event_type}
              </p>
            </div>
            
            {/* Email de destination */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email de destination
              </label>
              <Input
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder={user?.email || "votre@email.com"}
              />
              <p className="text-xs text-gray-500">
                Si vide, l'email sera envoyé à votre adresse ({user?.email})
              </p>
            </div>
            
            {/* Info sur les données de test */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <p className="font-medium">Email de test</p>
                  <p className="mt-1">
                    Les variables du template seront remplacées par des données fictives 
                    pour vérifier le rendu et la mise en forme.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowTestEmailDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={sendTestEmail} 
            disabled={sendingTestEmail}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {sendingTestEmail ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Envoyer le test
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Templates de Notification
          </h3>
          <p className="text-sm text-gray-500">
            Personnalisez les messages envoyés automatiquement
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
          <Button onClick={createNewTemplate}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Template
          </Button>
        </div>
      </div>
      
      {/* Info sur les variables */}
      <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
        <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-purple-800">
          <p className="font-medium">Utilisez des variables pour personnaliser vos messages</p>
          <p className="mt-1">
            Exemple: <code className="bg-purple-100 px-1 rounded">{'{actor_name}'}</code> sera remplacé par le nom de l&apos;utilisateur.
            Cliquez sur une variable dans l&apos;éditeur pour la copier.
          </p>
        </div>
      </div>
      
      {/* Liste des templates */}
      {templates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Aucun template configuré</p>
          <Button className="mt-4" onClick={createNewTemplate}>
            <Plus className="w-4 h-4 mr-2" />
            Créer un template
          </Button>
        </div>
      ) : (
        renderTemplatesList()
      )}
      
      {/* Modals */}
      {renderEditor()}
      {renderPreviewModal()}
      {renderTestEmailModal()}
    </div>
  );
};

export default NotificationTemplates;
